import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Airplane, Heartbeat, Student, Wheelchair, ArrowRight, CheckCircle, Clock, MapPin, Hospital,
} from "@phosphor-icons/react";
import PageBackLink from "../components/PageBackLink";

const SERVICES = {
  airport: {
    icon: Airplane,
    accent: "from-brandblue to-ova",
    title: "Airport medical transport",
    kicker: "Flight-aware NEMT · Nationwide airport coverage",
    tagline: "Landed. Loaded. Home. We watch the flight so you don't have to.",
    hero: "Send us a flight number — we automatically rebuild the pickup time to landing + buffer, surface delay alerts to the family, and stage a wheelchair or stretcher curbside or at baggage claim.",
    features: [
      ["Flight-number auto-adjust", "Pickup time recalculates when the carrier pushes a delay"],
      ["Meet-and-greet or curbside", "Driver holds a sign at baggage; or curbside for Medallion pickups"],
      ["Wheelchair & stretcher options", "Lifts, oxygen-secure, door-through-door escorts"],
      ["Live tracker link auto-texted", "Family opens a public link — no login"],
    ],
    useCases: ["Post-op travel home", "Dialysis patients flying in for treatment", "Out-of-state specialist visits", "Hospice transport to airline"],
    cta: "Book an airport trip",
  },
  dialysis: {
    icon: Heartbeat,
    accent: "from-brandblue to-forest",
    title: "Dialysis transport",
    kicker: "3x/week · Monday–Saturday",
    tagline: "A standing dialysis ride is the most important one on a patient's calendar. We don't miss.",
    hero: "MedexOVA runs recurring dialysis schedules for centers nationwide with <2% miss rate. We work directly with your clinic's schedulers or Medicaid MCO to keep the standing series on autopilot.",
    features: [
      ["Recurring schedule builder", "M/W/F or T/Th/Sat — skip your center's holiday list"],
      ["ID-matched driver handoff", "Named rider, mobility, preferred chair, MCO authorization"],
      ["Session-over-run re-dispatch", "If dialysis runs long, we roll the return trip automatically"],
      ["Facility billing statements", "Monthly CSV + PDF, trip-by-trip with miles and signatures"],
    ],
    useCases: ["Free-standing dialysis centers", "Hospital-based in-center hemo", "Home-health peritoneal training visits", "Transplant clinic follow-ups"],
    cta: "Set up a dialysis series",
  },
  school: {
    icon: Student,
    accent: "from-ova to-brandblue",
    title: "IDD / Special-needs school transport",
    kicker: "K-12 · STAAR approved · TX compliant",
    tagline: "Routes that treat your riders like people, not stops.",
    hero: "MedexOVA partners with school districts and IDD day programs — starting in Texas and expanding nationwide — to run small-van routes with trained drivers, fixed seat assignments, and live parent tracking links.",
    features: [
      ["Same driver every day", "Familiarity matters for riders on the spectrum"],
      ["Fixed seat assignments", "Color-coded seat chart inside each van"],
      ["Parent live-track SMS", "Morning and afternoon run links auto-sent"],
      ["Bell-time integration", "Adjusts for early release, testing days, weather"],
    ],
    useCases: ["ARD-mandated transport", "Non-public school placements", "IDD day-habilitation programs", "Summer ESY routes"],
    cta: "Talk to school routing",
  },
  wheelchair: {
    icon: Wheelchair,
    accent: "from-forest to-brandblue",
    title: "Wheelchair transport",
    kicker: "Q-Straint secured · TX NEMT compliant",
    tagline: "Lift-equipped vans with drivers trained to actually use them.",
    hero: "Not every wheelchair trip is equal — we match chair size, weight rating, and preferred boarding (front/rear lift) to each rider. Drivers carry Q-Straint L-track certification.",
    features: [
      ["Manual + power chair rated", "Up to 600 lb combined rider + chair"],
      ["Q-Straint 4-point tie-down", "Every ride. No exceptions."],
      ["Door-through-door option", "Driver assists inside the home or facility"],
      ["Bariatric on request", "30-inch chair and lift compatible"],
    ],
    useCases: ["Dialysis, infusion, chemo", "Post-discharge to home or SNF", "Facility-to-facility transfers", "Appointments, labs, imaging"],
    cta: "Book a wheelchair ride",
  },
};

export default function ServiceLanding() {
  const { slug } = useParams();
  const svc = SERVICES[slug];
  if (!svc) return <Navigate to="/" replace />;
  const Icon = svc.icon;
  return (
    <main className="min-h-screen bg-bone" data-testid={`service-landing-${slug}`}>
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "16px 24px 0", maxWidth: "72rem" }} />
      <section className="relative overflow-hidden bg-brandblue-ink text-white">
        <div aria-hidden className="orb h-[540px] w-[540px] bg-brandblue/30 -top-40 -left-40" />
        <div aria-hidden className="orb h-[400px] w-[400px] bg-ova/25 -bottom-20 -right-10" />
        <div aria-hidden className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">
            <Link to="/" className="hover:text-white">MedexOVA</Link> <span className="text-white/30">/</span>
            <span className="text-white/60">Services</span> <span className="text-white/30">/</span>
            <span className="text-white/80">{svc.title}</span>
          </div>
          <div className={`mt-6 inline-flex h-14 w-14 rounded-xl bg-gradient-to-br ${svc.accent} items-center justify-center ring-1 ring-white/20`}>
            <Icon size={26} weight="fill" className="text-white" />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mt-6 font-heading text-4xl sm:text-6xl font-black leading-[1.05] max-w-4xl"
          >
            {svc.tagline}
          </motion.h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base leading-relaxed">{svc.hero}</p>
          <div className="mt-3 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">{svc.kicker}</div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" data-testid="service-primary-cta" className="inline-flex items-center gap-2 bg-white text-brandblue-ink font-bold px-6 py-3.5 rounded-md hover:bg-white/90 text-sm">
              {svc.cta} <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/partners" className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 font-semibold px-6 py-3.5 rounded-md text-sm">
              Or set up as a facility
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-3xl font-black text-ink">What makes it different</h2>
            <ul className="mt-6 space-y-5">
              {svc.features.map(([t, sub], i) => (
                <motion.li
                  key={t}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-3">
                  <CheckCircle size={20} weight="fill" className="mt-0.5 text-brandblue shrink-0" />
                  <div>
                    <div className="font-semibold text-ink">{t}</div>
                    <div className="text-sm text-slate-500">{sub}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-3xl font-black text-ink">Common use cases</h2>
            <div className="mt-6 grid gap-3">
              {svc.useCases.map((u, i) => (
                <motion.div
                  key={u}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -2, boxShadow: "0 8px 24px -12px rgba(15,23,42,0.35)" }}
                  className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3">
                  <Hospital size={16} className="mt-0.5 text-brandblue" />
                  <span className="text-sm text-slate-700">{u}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 rounded-2xl bg-gradient-to-br from-brandblue-ink via-brandblue-ink to-[#0A1730] text-white p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center relative overflow-hidden">
          <div aria-hidden className="orb h-64 w-64 bg-brandblue/30 -top-10 right-1/3" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue flex items-center gap-2">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"/><span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"/></span>
              Ready to book?
            </div>
            <h3 className="mt-2 font-heading text-3xl font-black">We can have a driver at pickup in under 60 minutes on most days.</h3>
            <p className="mt-3 text-white/70 text-sm max-w-2xl">Open the booking form, or ask <b>Ava</b> (bottom-right) to do it for you in chat.</p>
          </div>
          <Link to="/book" className="relative inline-flex items-center gap-2 bg-white text-brandblue-ink font-bold px-6 py-3.5 rounded-md text-sm hover:-translate-y-0.5 transition-transform">
            Book now <ArrowRight size={14} weight="bold" />
          </Link>
        </motion.div>
      </section>
    </main>
  );
}
